import {useCallback, useRef, useState} from 'react';
import {
  decisionForFailedVerify,
  reduceVerifyResponse,
  VerifyDecision,
} from '../utils/puzzle/reduceVerifyResponse';
import {
  trackPuzzle,
  trackPuzzleError,
  watchRequestTiming,
} from '../utils/puzzleTelemetry';

/** A verify call still unanswered after this long is reported as stuck. */
const STUCK_AFTER_MS = 5000;

interface Move {
  san: string;
  newFen: string;
}

interface Options {
  /** Posts the move for verification. */
  send: (move: Move) => Promise<any>;
  /** URL fragment of the verify request, to report its browser timing. */
  timingUrlPart?: string;
  /** Board FENs at the time the response arrives (includes the dropped move). */
  getFens: () => {fen: string; prevFen: string};
  timeOver: boolean;
  onDecision: (decision: VerifyDecision, response?: any) => void;
}

/**
 * Locks the board while a move is being verified and always gives it back:
 * every outcome (answer, odd answer, rejected request) goes through a decision.
 */
export default function usePuzzleMoveFlow({
  send,
  timingUrlPart,
  getFens,
  timeOver,
  onDecision,
}: Options) {
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const pending = useRef(false);
  const timeOverRef = useRef(timeOver);
  timeOverRef.current = timeOver;

  const drop = useCallback(
    (move: Move) => {
      if (timeOverRef.current || pending.current) {
        return;
      }
      pending.current = true;
      setIsPlayerTurn(false);

      const startedAt = Date.now();
      const timing = timingUrlPart ? watchRequestTiming(timingUrlPart) : null;
      let stuck = false;
      trackPuzzle('classroom_puzzle_move_sent', {san: move.san});
      const stuckTimer = setTimeout(() => {
        stuck = true;
        trackPuzzle('classroom_puzzle_move_stuck', {
          san: move.san,
          waited_ms: Date.now() - startedAt,
        });
      }, STUCK_AFTER_MS);

      const finish = (decision: VerifyDecision, response?: any) => {
        clearTimeout(stuckTimer);
        pending.current = false;
        timing?.report('classroom_puzzle_move_timing', {
          san: move.san,
          duration_ms: Date.now() - startedAt,
          was_stuck: stuck,
        });
        trackPuzzle('classroom_puzzle_move_verified', {
          duration_ms: Date.now() - startedAt,
          was_stuck: stuck,
          valid: response?.valid,
          incorrect: response?.incorrect,
          solved: decision.solved,
          ignored: decision.ignored,
          unlocked: decision.unlock,
        });
        if (decision.ignored) {
          return;
        }
        if (decision.unlock) {
          setIsPlayerTurn(true);
        }
        onDecision(decision, response);
      };

      send(move).then(
        response => {
          const {fen, prevFen} = getFens();
          finish(
            reduceVerifyResponse({
              response,
              fen,
              prevFen,
              timeOver: timeOverRef.current,
            }),
            response,
          );
        },
        err => {
          trackPuzzleError('classroom_puzzle_move_failed', err, {
            duration_ms: Date.now() - startedAt,
            was_stuck: stuck,
          });
          finish(decisionForFailedVerify(getFens().prevFen));
        },
      );
    },
    [send, timingUrlPart, getFens, onDecision],
  );

  return {isPlayerTurn, setIsPlayerTurn, drop};
}
